import GuideLayout, { type TocItem, type RelatedCalculator } from './GuideLayout';

export interface ExamDetails {
  examCode: string;
  examName: string;
  passingScore: number;
  examCostGBP: number;
  retirementDate?: string;
  skillsMeasured: string[];
}

interface CertificationLayoutProps {
  title: string;
  description: string;
  lastUpdated: string;
  tocItems: TocItem[];
  examDetails: ExamDetails;
  relatedCalculators?: RelatedCalculator[];
  topCta?: React.ReactNode;
  bottomCta?: React.ReactNode;
  children: React.ReactNode;
  canonical?: string;
  metaTitle?: string;
  metaDescription?: string;
}

function ExamDetailsCard({ exam }: { exam: ExamDetails }) {
  return (
    <div className="rounded p-5 flex flex-col gap-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
      <div className="flex items-center gap-3">
        <span className="material-symbols-outlined text-[#0078d4] text-[28px]">workspace_premium</span>
        <div>
          <p className="text-[12px] uppercase tracking-widest font-bold" style={{ color: 'var(--text-tertiary)' }}>Exam Details</p>
          <p className="text-[15px] font-bold" style={{ color: 'var(--text-primary)' }}>{exam.examCode}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-[13px]">
        <div className="flex justify-between">
          <span style={{ color: 'var(--text-tertiary)' }}>Exam name</span>
          <span className="text-right max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{exam.examName}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--text-tertiary)' }}>Passing score</span>
          <span style={{ color: 'var(--text-secondary)' }}>{exam.passingScore} / 1000</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--text-tertiary)' }}>Exam cost</span>
          <span style={{ color: 'var(--text-secondary)' }}>£{exam.examCostGBP}</span>
        </div>
        {exam.retirementDate && (
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-tertiary)' }}>Retires</span>
            <span className="text-[#ffb900]">{exam.retirementDate}</span>
          </div>
        )}
      </div>

      {exam.skillsMeasured.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--text-tertiary)' }}>
            Skills measured
          </p>
          <ul className="flex flex-col gap-1">
            {exam.skillsMeasured.map((skill, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                <span className="text-[#0078d4] mt-0.5 shrink-0">›</span>
                {skill}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProminentAffiliateCTA({ cta }: { cta: React.ReactNode }) {
  return (
    <div className="border-2 border-[#0078d4] rounded p-5" style={{ backgroundColor: 'var(--surface-container-low)' }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined text-[#0078d4] text-[20px]">school</span>
        <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: '#c7e0f4' }}>
          Recommended study resources
        </p>
      </div>
      {cta}
    </div>
  );
}

export default function CertificationLayout({
  title,
  description,
  lastUpdated,
  tocItems,
  examDetails,
  relatedCalculators = [],
  topCta,
  bottomCta,
  children,
  canonical,
  metaTitle,
  metaDescription,
}: CertificationLayoutProps) {
  // Wrap children with the exam details sidebar injected on desktop
  const contentWithSidebar = (
    <div className="flex gap-6 items-start">
      <div className="flex-1 min-w-0">{children}</div>

      {/* Exam details sidebar — visible on lg+ */}
      <aside className="hidden lg:flex flex-col gap-4 w-64 shrink-0 sticky top-24">
        <ExamDetailsCard exam={examDetails} />

        {topCta && (
          <ProminentAffiliateCTA cta={topCta} />
        )}
      </aside>
    </div>
  );

  // On mobile, render exam details card inline above children
  const mobileExamCard = (
    <div className="lg:hidden mb-6">
      <ExamDetailsCard exam={examDetails} />
      {topCta && (
        <div className="mt-4">
          <ProminentAffiliateCTA cta={topCta} />
        </div>
      )}
    </div>
  );

  return (
    <GuideLayout
      title={title}
      description={description}
      lastUpdated={lastUpdated}
      tocItems={tocItems}
      relatedCalculators={relatedCalculators}
      bottomCta={bottomCta}
      canonical={canonical}
      metaTitle={metaTitle}
      metaDescription={metaDescription}
    >
      {mobileExamCard}
      {contentWithSidebar}
    </GuideLayout>
  );
}
